import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/*
  Per-page <title> and meta description.

  public/index.html carries one set of tags for the whole site, so every
  route — the cart, the blog, a policy page — announced itself as
  "Premium Astrological Gemstones". That is what a search result and a
  shared link both read from, and it is what a screen reader says when
  the page changes.

  Route-matched rather than set by each component, so nobody has to
  remember to add it to a new page, and so the browser tab updates in
  the same tick as the navigation.
*/

const SITE = 'Celestial Stones';

/* The host the site actually resolves to — celestialstones.in answers a
   308 redirect to this one, so a canonical pointing at the bare domain
   names a redirect rather than a page. */
const ORIGIN = 'https://www.celestialstones.in';

const DESCRIPTIONS = [
  [
    /^\/$/,
    'Lab-Certified Astrological Gemstones',
    'Lab-certified gemstones and bracelets chosen for your ruling planet. Free shipping across India.',
  ],
  [
    /^\/shop/,
    'Shop Gemstones & Bracelets',
    'Browse lab-certified gemstone bracelets, rings, pendants and malas, filtered by planet or type.',
  ],
  [
    /^\/cart/,
    'Your Bag',
    'Review the stones in your bag and check out over WhatsApp.',
  ],
  [
    /^\/blog\/.+/,
    'Journal',
    'Guides to gemstones, planets and how to wear your stones.',
  ],
  [
    /^\/blog/,
    'Journal',
    'Guides to gemstones, planets and how to wear your stones.',
  ],
  [
    /^\/planets/,
    'Shop by Planet',
    'Find the gemstones aligned with each of the nine planets, and the stone that suits your birth number.',
  ],
  [
    /^\/privacy-policy/,
    'Privacy Policy',
    'How Celestial Stones collects, uses and protects your personal data, and how to have it deleted.',
  ],
  [
    /^\/cookie-policy/,
    'Cookie Policy',
    'The cookies Celestial Stones uses, what each one is for, and how to change your choice.',
  ],
  [
    /^\/shipping-returns/,
    'Shipping & Returns',
    'Free delivery across India and our 7-day return policy on unused items.',
  ],
  [
    /^\/terms-of-service/,
    'Terms of Service',
    'The terms that apply when you order from Celestial Stones.',
  ],
  [
    /^\/data-request/,
    'Data & Privacy Requests',
    'Request a copy of your personal data, or ask us to delete it.',
  ],
  [
    /^\/unsubscribe/,
    'Unsubscribe',
    'Remove your email address from the Celestial Stones mailing list.',
  ],
  [
    /^\/account/,
    'My Account',
    'Your orders, saved address and account details.',
  ],
];

const FALLBACK = [
  'Lab-Certified Astrological Gemstones',
  'Lab-certified gemstones and bracelets chosen for your ruling planet.',
];

const setMeta = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

/*
  index.html carries a single <link rel="canonical"> and nothing ever
  moved it, so /shop, /blog and every policy page each told Google "the
  real version of me is the home page" — an instruction to fold them
  into it and index only one URL.

  The query string is deliberately dropped: /shop?planet=Mars is the
  shop filtered, not a separate page.
*/
const setCanonical = (pathname) => {
  let tag = document.querySelector('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  // Trailing slash on the home page only, matching how the site serves.
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  tag.setAttribute('href', `${ORIGIN}${path}`);
};

export default function PageMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    // The admin panel is private; it has no business in search results.
    if (pathname.startsWith('/admin')) {
      document.title = `Admin · ${SITE}`;
      setMeta('robots', 'noindex, nofollow');
      return;
    }

    const match = DESCRIPTIONS.find(([pattern]) => pattern.test(pathname));
    const [title, description] = match ? match.slice(1) : FALLBACK;

    document.title = `${title} · ${SITE}`;
    setMeta('description', description);
    setMeta('robots', 'index, follow');
    setCanonical(pathname);
  }, [pathname]);

  return null;
}
